import React, { useCallback, useEffect, useState } from "react";
import { BarChart3, Download, Filter } from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadCSV, money } from "@/lib/format";
import { PageHeader } from "@/components/common/PageHeader";
import { StatCard } from "@/components/common/StatCard";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Field } from "@/pages/crm/LeadForm";
import { cn } from "@/lib/utils";

const RANGES = [
  { key: "all", label: "All time" }, { key: "today", label: "Today" }, { key: "week", label: "This week" },
  { key: "month", label: "This month" }, { key: "prev_month", label: "Previous month" },
  { key: "quarter", label: "This quarter" }, { key: "year", label: "This year" }, { key: "custom", label: "Custom range" },
];

const iso = (d) => d.toISOString().slice(0, 10);

const rangeToDates = (key) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  switch (key) {
    case "today": return [iso(now), iso(now)];
    case "week": { const s = new Date(now); s.setDate(now.getDate() - now.getDay()); return [iso(s), iso(now)]; }
    case "month": return [iso(new Date(y, m, 1)), iso(now)];
    case "prev_month": return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
    case "quarter": return [iso(new Date(y, Math.floor(m / 3) * 3, 1)), iso(now)];
    case "year": return [iso(new Date(y, 0, 1)), iso(now)];
    default: return ["", ""];
  }
};

const Tip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 shadow-md">
      <p className="text-xs font-semibold">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="num text-sm" style={{ color: p.color }}>
          {p.name}: {currency && p.dataKey === "value" ? money(p.value, true) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [employee, setEmployee] = useState("all");
  const [source, setSource] = useState("all");
  const [staff, setStaff] = useState([]);
  const [demos, setDemos] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [f, t] = range === "custom" ? [custom.from, custom.to] : rangeToDates(range);
    try {
      const { data } = await api.get("/analytics", {
        params: { date_from: f || undefined, date_to: t || undefined, employee, source },
      });
      setData(data);
      api.get("/demos/analytics/team", { params: { date_from: f || undefined, date_to: t || undefined } })
        .then((r) => setDemos(r.data)).catch(() => {});
    } finally { setLoading(false); }
  }, [range, custom, employee, source]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/staff/assignable").then((r) => setStaff(r.data)).catch(() => {}); }, []);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-20 w-full rounded-xl" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-80 w-full rounded-xl" />
      </div>
    );
  }

  const k = data.kpis;
  const cards = [
    { label: "Total Leads", value: k.total_leads, icon: "Users", tone: "primary" },
    { label: "Leads Contacted", value: k.contacted, icon: "PhoneCall", tone: "info" },
    { label: "With Follow-ups", value: k.with_followups, icon: "CalendarCheck2", tone: "info" },
    { label: "Reached Quotation", value: k.stage_quotation, icon: "FileText", tone: "neutral" },
    { label: "Reached Proforma", value: k.stage_proforma, icon: "FileCheck2", tone: "neutral" },
    { label: "Converted to Invoice", value: k.stage_invoice, icon: "ReceiptIndianRupee", tone: "success" },
    { label: "Total Invoice Value", value: k.invoice_value, icon: "TrendingUp", tone: "success", currency: true },
    { label: "Lost Leads", value: k.lost_leads, icon: "UserX", tone: "danger" },
  ];

  const rates = [
    { label: "Lead to Quotation", value: k.lead_to_quotation },
    { label: "Quotation to Invoice", value: k.quotation_to_invoice },
    { label: "Overall Lead to Invoice", value: k.lead_to_invoice },
  ];

  const demoColumns = [
    { key: "name", label: "Team member", render: (r) => <span className="text-sm font-semibold">{r.name}</span> },
    { key: "demos", label: "Sessions", align: "right", render: (r) => <span className="num">{r.demos}</span> },
    { key: "completed", label: "Delivered", align: "right", render: (r) => <span className="num text-success">{r.completed}</span> },
    { key: "days", label: "Field days", align: "right", render: (r) => <span className="num">{r.days}</span> },
    { key: "avg_rating", label: "Avg rating", align: "right", render: (r) => <span className="num font-semibold text-primary">{r.avg_rating || "\u2014"}</span> },
    { key: "converted", label: "Converted", align: "right", render: (r) => <span className="num">{r.converted}</span>, hideMobile: true },
    { key: "conversion", label: "Conv %", align: "right", render: (r) => <span className="num font-semibold">{r.conversion}%</span> },
    { key: "cancelled", label: "Cancelled", align: "right", render: (r) => <span className="num text-destructive">{r.cancelled}</span>, hideMobile: true },
    { key: "expense", label: "Spend", align: "right", render: (r) => <span className="num">{money(r.expense, true)}</span> },
    { key: "cost_per_demo", label: "Per session", align: "right", render: (r) => <span className="num">{money(r.cost_per_demo, true)}</span>, hideMobile: true },
  ];

  const perfColumns = [
    { key: "name", label: "Employee", render: (r) => <div><p className="text-sm font-semibold">{r.name}</p><p className="text-xs capitalize text-muted-foreground">{r.role}</p></div> },
    { key: "assigned", label: "Assigned", align: "right", render: (r) => <span className="num">{r.assigned}</span> },
    { key: "created", label: "Created", align: "right", render: (r) => <span className="num">{r.created}</span>, hideMobile: true },
    { key: "followups_done", label: "FU done", align: "right", render: (r) => <span className="num text-success">{r.followups_done}</span> },
    { key: "followups_missed", label: "FU missed", align: "right", render: (r) => <span className="num text-destructive">{r.followups_missed}</span> },
    { key: "quotations", label: "Quotes", align: "right", render: (r) => <span className="num">{r.quotations}</span>, hideMobile: true },
    { key: "invoices", label: "Invoices", align: "right", render: (r) => <span className="num">{r.invoices}</span> },
    { key: "invoice_value", label: "Value", align: "right", render: (r) => <span className="num font-semibold">{money(r.invoice_value, true)}</span> },
    { key: "conversion", label: "Conv %", align: "right", render: (r) => <span className="num font-semibold text-primary">{r.conversion}%</span> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={BarChart3}
        title="Analytics"
        subtitle="Company wide performance. Visible to administrators only."
        actions={
          <Button variant="outline" onClick={() => { downloadCSV(data.performance, "employee-performance.csv"); toast.success("Performance exported"); }}>
            <Download className="h-4 w-4" /> Export performance
          </Button>
        }
      />

      {/* Filters */}
      <Card>
        <CardContent className="grid gap-4 py-5 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Period">
            <Select value={range} onValueChange={setRange}>
              <SelectTrigger data-testid="analytics-range"><SelectValue /></SelectTrigger>
              <SelectContent>{RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {range === "custom" ? (
            <>
              <Field label="From"><Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} /></Field>
              <Field label="To"><Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} /></Field>
            </>
          ) : (
            <Field label="Lead source">
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All sources</SelectItem>
                  {(data.sources || []).map((s) => <SelectItem key={s.source} value={s.source}>{s.source}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Employee">
            <Select value={employee} onValueChange={setEmployee}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All employees</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c, i) => <StatCard key={c.label} index={i} {...c} />)}
      </div>

      {/* Conversion rates */}
      <div className="grid gap-4 sm:grid-cols-3">
        {rates.map((r) => (
          <Card key={r.label}>
            <CardContent className="py-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{r.label}</p>
              <p className="num mt-2 text-3xl font-bold text-primary">{r.value}%</p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-gradient-warm transition-all duration-700" style={{ width: `${Math.min(100, r.value)}%` }} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Funnel */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="font-display text-base">Lead funnel</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.funnel.map((f, i) => {
                const max = data.funnel[0]?.count || 1;
                const pct = Math.round((f.count / max) * 100);
                return (
                  <div key={f.stage}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="font-medium">{f.stage}</span>
                      <span className="num text-muted-foreground">{f.count} . {pct}%</span>
                    </div>
                    <div className="h-7 overflow-hidden rounded-md bg-muted">
                      <div
                        className="flex h-full items-center rounded-md transition-all duration-700"
                        style={{ width: `${Math.max(4, pct)}%`, background: `hsl(var(--chart-${(i % 5) + 1}))` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Monthly trend */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="font-display text-base">Monthly lead trend</CardTitle></CardHeader>
          <CardContent>
            {data.monthly.length === 0 ? <EmptyState title="Not enough data yet" /> : (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data.monthly} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gLeads" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--chart-1))" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <Tooltip content={<Tip />} />
                    <Area type="monotone" dataKey="leads" name="Leads" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#gLeads)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Monthly invoice value */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="font-display text-base">Monthly invoice value</CardTitle></CardHeader>
          <CardContent>
            {data.monthly.length === 0 ? <EmptyState title="No invoices in this period" /> : (
              <div className="h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={data.monthly} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => money(v, true)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip content={<Tip currency />} />
                    <Line type="monotone" dataKey="value" name="Invoice value" stroke="hsl(var(--chart-3))" strokeWidth={2.5} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead source */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="font-display text-base">Lead source performance</CardTitle></CardHeader>
          <CardContent>
            {data.sources.length === 0 ? <EmptyState title="No source data" /> : (
              <div className="flex flex-col items-center gap-4 sm:flex-row">
                <div className="h-[220px] w-full sm:w-1/2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={data.sources} dataKey="leads" nameKey="source" innerRadius={48} outerRadius={82} paddingAngle={3}>
                        {data.sources.map((_, i) => <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                      </Pie>
                      <Tooltip content={<Tip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-full space-y-2 sm:w-1/2">
                  {data.sources.map((s, i) => (
                    <div key={s.source} className="flex items-center gap-2 text-sm">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: `hsl(var(--chart-${(i % 5) + 1}))` }} />
                      <span className="flex-1 truncate">{s.source}</span>
                      <span className="num text-muted-foreground">{s.leads}</span>
                      <span className="num w-12 text-right text-xs text-success">{s.converted} won</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Product-wise */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="font-display text-base">Product-wise sales</CardTitle></CardHeader>
          <CardContent>
            {data.products.length === 0 ? <EmptyState title="No product data yet" /> : (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.products} margin={{ top: 8, right: 8, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="product" angle={-22} textAnchor="end" interval={0} height={70}
                      tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={(v) => money(v, true)} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={70} />
                    <Tooltip content={<Tip currency />} cursor={{ fill: "hsl(var(--primary) / 0.06)" }} />
                    <Bar dataKey="value" name="Value" radius={[6, 6, 0, 0]} maxBarSize={54}>
                      {data.products.map((_, i) => <Cell key={i} fill={`hsl(var(--chart-${(i % 5) + 1}))`} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Follow-up performance */}
      <div className="grid gap-4 sm:grid-cols-3">
        {data.followup_performance.map((f) => (
          <Card key={f.name}>
            <CardContent className="flex items-center justify-between py-5">
              <span className="text-sm font-medium">{f.name} follow-ups</span>
              <span className={cn("num text-2xl font-bold",
                f.name === "Overdue" ? "text-destructive" : f.name === "Completed" ? "text-success" : "text-info")}>
                {f.count}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Employee performance */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base">Employee performance</CardTitle>
          <p className="text-sm text-muted-foreground">Shown only here, never on the regular dashboard.</p>
        </CardHeader>
        <CardContent>
          <DataTable columns={perfColumns} rows={data.performance}
            empty={<EmptyState title="No staff data" description="Add staff members to compare their performance." />} />
        </CardContent>
      </Card>

      {/* Demo & training productivity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base">Demo &amp; training productivity</CardTitle>
          <p className="text-sm text-muted-foreground">
            {demos
              ? `${demos.totals.completed} of ${demos.totals.demos} session(s) delivered \u00b7 ${demos.totals.days} day(s) in the field \u00b7 average customer rating ${demos.totals.avg_rating || "\u2014"}/5 \u00b7 ${money(demos.totals.expense, true)} spent \u00b7 ${demos.totals.conversion}% converted`
              : "Loading the demo numbers..."}
          </p>
        </CardHeader>
        <CardContent>
          <DataTable columns={demoColumns} rows={demos?.team || []}
            empty={<EmptyState title="No demos yet"
              description="Once your team runs a demo or training, their productivity shows up here." />} />
        </CardContent>
      </Card>
    </div>
  );
}
